/**
 * useImprovementConversation — the data hook behind the improvement conversation panel.
 *
 * Owns one conversation: the first landlord message starts it (POST, or reopens the anchor's open
 * conversation when the start answers `existing`), later messages are sent to it, and the view is
 * read from the relay. A new reply is announced by the `agent.conversation_turn_ready` domain event
 * (DC-6), which triggers a refetch; while a turn is running and the socket is down the view is
 * polled every 5 seconds instead.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ImprovementStartBody } from '@resida/api-types/agent';
import {
  confirmImprovementProposal,
  getImprovementConversation,
  improvementConversationQueryKey,
  improvementErrorKind,
  notQuiteImprovementProposal,
  rerouteImprovementConversation,
  sendImprovementMessage,
  startImprovementConversation,
  type ImprovementConversationView,
  type ImprovementErrorKind,
  type ImprovementProposalView,
} from '../../../api/improvement-conversations.api';
import { AGENT_PLAYBOOKS_QUERY_KEY } from '../AgentSettingsLink';
import { useCentrifugoContext } from '../../../contexts/CentrifugoContext';
import { useDomainEvents } from '../../../hooks/useDomainEvents';

/** How a conversation is started: the entry, what it is about, and the agent it improves. */
export interface ConversationStart {
  entry: ImprovementStartBody['entry'];
  anchor: ImprovementStartBody['anchor'];
  caseId?: string;
}

/** Polling interval while a turn is running and the realtime socket is disconnected. */
const TURN_POLL_INTERVAL_MS = 5_000;

/** What the landlord just confirmed, kept so the outcome can restate the rule after the proposal is gone. */
export interface ConfirmedChange {
  proposal: ImprovementProposalView;
}

interface UseImprovementConversationArgs {
  conversationId?: string;
  start?: ConversationStart;
  onStartFailed?: (kind: ImprovementErrorKind) => void;
}

export function useImprovementConversation({ conversationId: initialId, start, onStartFailed }: UseImprovementConversationArgs) {
  const queryClient = useQueryClient();
  const { isConnected, isSubscribed } = useCentrifugoContext();
  const [conversationId, setConversationId] = useState<string | undefined>(initialId);
  const [error, setError] = useState<ImprovementErrorKind | null>(null);
  // Which kind of call raised `error`: a message (its text can be re-sent) or a proposal action (the
  // proposal stays on screen and its own buttons retry, e.g. Confirm after a 503, IMP-BR-10 / CR-6).
  const [errorSource, setErrorSource] = useState<'message' | 'action'>('message');
  const [optimistic, setOptimistic] = useState<{ text: string; baseTurns: number } | null>(null);
  const [lastSent, setLastSent] = useState<string | null>(null);
  // The text of a send that never reached the agent, so the panel can put it back in the reply box.
  const [unsentText, setUnsentText] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<ConfirmedChange | null>(null);

  const query = useQuery({
    queryKey: improvementConversationQueryKey(conversationId ?? ''),
    queryFn: () => getImprovementConversation(conversationId!),
    enabled: conversationId !== undefined,
    retry: false,
    // A reply can land while the tab is in the background; coming back refreshes the conversation.
    refetchOnWindowFocus: true,
    refetchInterval: (q) =>
      q.state.data?.state === 'turn_running' && !(isConnected && isSubscribed) ? TURN_POLL_INTERVAL_MS : false,
  });
  const view: ImprovementConversationView | undefined = query.data;
  const gone = query.isError && improvementErrorKind(query.error) === 'gone';
  const readFailed = query.isError && !gone;

  const refetch = query.refetch;
  useDomainEvents({
    'agent.conversation_turn_ready': useCallback(
      (event: { conversationId: string }) => {
        if (event.conversationId === conversationId) void refetch();
      },
      [conversationId, refetch],
    ),
  });

  // The optimistic landlord turn is replaced once the read carries more turns than before it was sent.
  useEffect(() => {
    if (optimistic && view && view.turns.length > optimistic.baseTurns) setOptimistic(null);
  }, [optimistic, view]);

  const baseTurns = view?.turns.length ?? 0;
  const lastTurn = view?.turns[view.turns.length - 1];

  const send = useMutation({
    mutationFn: async (text: string) => {
      if (conversationId === undefined) {
        if (!start) throw new Error('A conversation needs a start or an id');
        const started = await startImprovementConversation({ ...start, text });
        setConversationId(started.conversationId);
        // The anchor already has an open conversation: no new turn was created, so the text goes in as a message.
        if ('existing' in started) await sendImprovementMessage(started.conversationId, text);
        return started.conversationId;
      }
      await sendImprovementMessage(conversationId, text);
      return conversationId;
    },
    onMutate: (text) => {
      setError(null);
      setErrorSource('message');
      setUnsentText(null);
      setLastSent(text);
      // Re-posting a failed turn's identical text is the retry signal (IMP-BR-23): the runtime re-runs that turn
      // and adds no transcript turn, so there is no optimistic bubble — the landlord turn is already shown.
      const retry = view?.state === 'awaiting_landlord' && lastTurn?.role === 'landlord' && lastTurn.text === text;
      if (!retry) setOptimistic({ text, baseTurns });
      return { retry };
    },
    onSuccess: async (id, _text, context) => {
      const refreshed = queryClient.invalidateQueries({ queryKey: improvementConversationQueryKey(id) });
      // A retry has no optimistic turn to hold "thinking": stay pending until the read shows the re-run.
      if (context?.retry) await refreshed;
      else void refreshed;
    },
    onError: (err, text) => {
      setOptimistic(null);
      const kind = improvementErrorKind(err);
      if (kind !== 'conflict') setUnsentText(text);
      if (kind === 'conflict' && conversationId) {
        // A turn is already running or the conversation closed: show what the relay says now.
        void queryClient.invalidateQueries({ queryKey: improvementConversationQueryKey(conversationId) });
        return;
      }
      setError(kind);
      if (conversationId === undefined) onStartFailed?.(kind);
    },
  });

  const refreshView = useCallback(async () => {
    if (conversationId) await queryClient.invalidateQueries({ queryKey: improvementConversationQueryKey(conversationId) });
  }, [conversationId, queryClient]);

  const confirm = useMutation({
    mutationFn: ({ proposal, choice }: { proposal: ImprovementProposalView; choice?: 'replace' | 'keep_both' }) =>
      confirmImprovementProposal(conversationId!, proposal.proposalId, choice),
    onMutate: () => {
      setError(null);
      setErrorSource('action');
    },
    onSuccess: async (_result, { proposal }) => {
      setConfirmed({ proposal });
      void queryClient.invalidateQueries({ queryKey: AGENT_PLAYBOOKS_QUERY_KEY });
      await refreshView();
    },
    onError: async (err) => {
      const kind = improvementErrorKind(err);
      if (kind === 'conflict') await refreshView();
      setError(kind);
    },
  });

  const notQuite = useMutation({
    mutationFn: (proposal: ImprovementProposalView) => notQuiteImprovementProposal(conversationId!, proposal.proposalId),
    onMutate: () => {
      setError(null);
      setErrorSource('action');
    },
    onSuccess: refreshView,
    onError: async (err) => {
      const kind = improvementErrorKind(err);
      // 409: the proposal on screen is no longer the conversation's current one — show what the relay holds now.
      if (kind === 'conflict') await refreshView();
      setError(kind);
    },
  });

  const reroute = useMutation({
    mutationFn: (to: 'preference' | 'defect') => rerouteImprovementConversation(conversationId!, to),
    onMutate: () => {
      setError(null);
      setErrorSource('action');
    },
    onSuccess: refreshView,
    onError: (err) => setError(improvementErrorKind(err)),
  });

  const turns = useMemo(() => {
    const base = view?.turns ?? [];
    return optimistic
      ? [...base, { turnId: 'pending', role: 'landlord' as const, text: optimistic.text, at: new Date().toISOString() }]
      : base;
  }, [view, optimistic]);

  const thinking = send.isPending || optimistic !== null || view?.state === 'turn_running' || reroute.isPending;
  // A turn that failed leaves the conversation waiting on the landlord with their message unanswered (SM-turn_running-turn_failed).
  const turnFailed = !thinking && view?.state === 'awaiting_landlord' && lastTurn?.role === 'landlord';

  return {
    conversationId,
    view,
    turns,
    loading: conversationId !== undefined && query.isPending,
    gone,
    readFailed,
    retryRead: refetch,
    thinking,
    turnFailed,
    error,
    errorSource,
    unsentText,
    sending: send.isPending,
    confirming: confirm.isPending,
    busy: send.isPending || confirm.isPending || notQuite.isPending || reroute.isPending,
    confirmed,
    send: (text: string) => send.mutate(text),
    /** Re-sends the landlord's last message after a failed turn or a refused send. */
    retry: () => {
      const text = turnFailed ? lastTurn?.text : lastSent;
      if (text) send.mutate(text);
    },
    /** `choice` answers a shown conflict (BR-12): replace saves the removal with the proposal; absent = keep_both. */
    confirm: (proposal: ImprovementProposalView, choice?: 'replace' | 'keep_both') => confirm.mutate({ proposal, choice }),
    notQuite: (proposal: ImprovementProposalView) => notQuite.mutate(proposal),
    reroute: (to: 'preference' | 'defect') => reroute.mutate(to),
    clearError: () => setError(null),
  };
}
